import type { SupportedLanguage } from './index'

/**
 * Define localized routes here.
 * Each key maps to a route name, with translations for each language.
 *
 * Example:
 *   about: { it: 'chi-siamo', en: 'about-us' }
 *   → /it/chi-siamo, /en/about-us
 */
export const localizedRoutes = {
  home: {
    it: '',
    en: '',
    fr: '',
    es: '',
    de: '',
    pt: '',
    pl: '',
    ja: '',
  },
  about: {
    it: 'chi-siamo',
    en: 'about-us',
    fr: 'a-propos',
    es: 'sobre-nosotros',
    de: 'ueber-uns',
    pt: 'sobre-nos',
    pl: 'o-nas',
    ja: 'about-us',
  },
  breweries: {
    it: 'birrifici',
    en: 'breweries',
    fr: 'brasseries',
    es: 'cervecerias',
    de: 'brauereien',
    pt: 'cervejarias',
    pl: 'browary',
    ja: 'breweries',
  },
  news: {
    it: 'birra-per-passione',
    en: 'beer-passion',
    fr: 'biere-passion',
    es: 'cerveza-pasion',
    de: 'bier-leidenschaft',
    pt: 'cerveja-paixao',
    pl: 'piwo-pasja',
    ja: 'beer-passion',
  },
  contacts: {
    it: 'contatti',
    en: 'contacts',
    fr: 'contacts',
    es: 'contactos',
    de: 'kontakt',
    pt: 'contactos',
    pl: 'kontakt',
    ja: 'contacts',
  },
  work: {
    it: 'lavora-con-noi',
    en: 'work-with-us',
    fr: 'travailler-avec-nous',
    es: 'trabaja-con-nosotros',
    de: 'karriere',
    pt: 'trabalhe-connosco',
    pl: 'praca',
    ja: 'work-with-us',
  },
  privacy: {
    it: 'privacy',
    en: 'privacy',
    fr: 'confidentialite',
    es: 'privacidad',
    de: 'datenschutz',
    pt: 'privacidade',
    pl: 'prywatnosc',
    ja: 'privacy',
  },
  whistleblowing: {
    it: 'whistleblowing',
    en: 'whistleblowing',
    fr: 'whistleblowing',
    es: 'whistleblowing',
    de: 'whistleblowing',
    pt: 'whistleblowing',
    pl: 'whistleblowing',
    ja: 'whistleblowing',
  },
  accessibility: {
    it: 'accessibilita',
    en: 'accessibility',
    fr: 'accessibilite',
    es: 'accesibilidad',
    de: 'barrierefreiheit',
    pt: 'acessibilidade',
    pl: 'dostepnosc',
    ja: 'accessibility',
  },
  reservedArea: {
    it: 'area-riservata',
    en: 'reserved-area',
    fr: 'espace-reserve',
    es: 'area-reservada',
    de: 'geschuetzter-bereich',
    pt: 'area-reservada',
    pl: 'strefa-klienta',
    ja: 'reserved-area',
  },
} as const

export type RouteKey = keyof typeof localizedRoutes

/**
 * Get the localized path for a route
 */
export function getLocalizedPath(
  routeKey: RouteKey,
  lang: SupportedLanguage
): string {
  const path = localizedRoutes[routeKey][lang]
  return path ? `/${lang}/${path}` : `/${lang}`
}

/**
 * Find route key from a path segment
 */
export function findRouteKeyFromPath(
  pathSegment: string,
  lang: SupportedLanguage
): RouteKey | null {
  for (const [key, routes] of Object.entries(localizedRoutes)) {
    if (routes[lang] === pathSegment) {
      return key as RouteKey
    }
  }
  return null
}
